import { RefObject, useCallback } from 'react';

// antd's Input/TextArea refs wrap the native element differently depending
// on the component — TextArea nests it under resizableTextArea, Input
// exposes it directly as `.input`.
const getNativeElement = (ref: unknown): HTMLInputElement | HTMLTextAreaElement | undefined => {
  const instance = ref as { resizableTextArea?: { textArea?: HTMLTextAreaElement }; input?: HTMLInputElement } | null;
  return instance?.resizableTextArea?.textArea ?? instance?.input ?? undefined;
};

/**
 * Ported from Authentication76's useEmojiInsert — generic "insert this
 * emoji at the cursor" behavior for any antd Input or TextArea. Falls back
 * to appending at the end if the native element (or its selection) isn't
 * available yet.
 */
export function useEmojiInsert(
  inputRef: RefObject<unknown>,
  value: string | undefined,
  onChange: (next: string) => void
) {
  return useCallback(
    (emoji: string) => {
      const currentValue = value ?? '';
      const el = getNativeElement(inputRef.current);

      if (!el) {
        onChange(currentValue + emoji);
        return;
      }

      const start = el.selectionStart ?? currentValue.length;
      const end = el.selectionEnd ?? currentValue.length;
      const nextValue = currentValue.slice(0, start) + emoji + currentValue.slice(end);
      onChange(nextValue);

      requestAnimationFrame(() => {
        el.focus();
        const cursorPos = start + emoji.length;
        el.setSelectionRange(cursorPos, cursorPos);
      });
    },
    [inputRef, value, onChange]
  );
}
